/**
 * StartScene — title screen + email gate.
 *
 * Shown once on first load (no email in localStorage) and after every
 * restart. If the email is already saved, we skip the prompt and jump
 * straight to the game. This is the right UX for a festival QR-code
 * scenario: every score must be linked to a real email.
 *
 * The scene draws the title in-canvas (white-on-black, monospace) and
 * delegates the email entry to the HTML overlay (`#overlay-root`). When
 * the user clicks PLAY (or hits Space / Enter), we hide the overlay and
 * start GameScene.
 */
import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH } from "../Game";
import { ensurePlayerIdent } from "../../ui/overlay";
import { TEX } from "../textures";
import { Settings } from "../../settings";

import { toggleFullscreen } from "../systems/fullscreen";

export class StartScene extends Phaser.Scene {
  constructor() {
    super({ key: "StartScene" });
  }

  public create(): void {
    this.cameras.main.setBackgroundColor("#000000");

    const cx = GAME_WIDTH / 2;

    // Title Splash
    this.add.image(cx, GAME_HEIGHT * 0.40, TEX.SPLASH)
      .setOrigin(0.5)
      .setScale(0.35)
      .setDepth(-1)
      .setAlpha(1);

    // Title Text
    this.add.text(cx, GAME_HEIGHT * 0.15, "TRASHKY IN SPACE", {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: "36px",
      color: "#ffffff",
      stroke: "#000000",
      strokeThickness: 6,
    }).setOrigin(0.5).setDepth(10);

    // Prompt to play.
    const prompt = this.add.text(cx, GAME_HEIGHT * 0.65, "[ PRESS SPACE OR TAP TO START ]", {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: "24px",
      color: "#ffffff",
      stroke: "#000000",
      strokeThickness: 4,
    }).setOrigin(0.5);

    this.tweens.add({
      targets: prompt,
      alpha: 0.3,
      duration: 700,
      yoyo: true,
      repeat: -1,
    });

    // Subtitle — touch controls
    this.add.text(cx, GAME_HEIGHT - 55, "TOUCH: DRAG TO MOVE  /  FIRE  CHOMP  BEAM", {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: "11px",
      color: "#aaaaaa",
      align: "center",
      stroke: "#000000",
      strokeThickness: 4,
    }).setOrigin(0.5, 1);

    // Controls text — keyboard, split into two lines
    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 25, "KEYBOARD: WASD/ARROWS MOVE  J: FIRE  X: CHOMP\nC: CHARGE LASER  SPACE: PAUSE", {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: "11px",
      color: "#aaaaaa",
      align: "center",
      stroke: "#000000",
      strokeThickness: 4,
    }).setOrigin(0.5, 1);

    // Version string
    this.add.text(GAME_WIDTH - 10, GAME_HEIGHT - 10, typeof __VERSION__ !== 'undefined' ? __VERSION__ : "v0.0.1.X", {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: "8px",
      color: "#666666",
    }).setOrigin(1, 1);

    // If no email saved, show the overlay prompt now. Otherwise we jump
    // straight to the game after the user taps/keys.
    const root = document.getElementById("overlay-root");
    const hasEmail = ensurePlayerIdent(root);

    const sfxBtn = this.add.text(20, 20, `[${Settings.sfx ? 'X' : ' '}] SFX`, {
      fontFamily: '"Press Start 2P", monospace', fontSize: "16px", color: "#ffffff"
    }).setOrigin(0, 0).setInteractive().on('pointerdown', (_p: any, _x: any, _y: any, ev: any) => {
      ev.stopPropagation();
      Settings.sfx = !Settings.sfx;
      sfxBtn.setText(`[${Settings.sfx ? 'X' : ' '}] SFX`);
    });

    const bgmBtn = this.add.text(20, 50, `[${Settings.bgm ? 'X' : ' '}] MUSIC`, {
      fontFamily: '"Press Start 2P", monospace', fontSize: "16px", color: "#ffffff"
    }).setOrigin(0, 0).setInteractive().on('pointerdown', (_p: any, _x: any, _y: any, ev: any) => {
      ev.stopPropagation();
      Settings.bgm = !Settings.bgm;
      bgmBtn.setText(`[${Settings.bgm ? 'X' : ' '}] MUSIC`);
    });

    // Defer pointer-start until the overlay is no longer visible — otherwise
    // a tap on the email prompt would both submit the form AND jump us into
    // the game.
    this.bindStartInputs(hasEmail);
  }

  private bindStartInputs(hasEmail: boolean): void {
    const startNow = (): void => {
      // Re-enable keyboard for GameScene
      if (this.input.keyboard) {
        this.input.keyboard.enabled = true;
      }
      const root = document.getElementById("overlay-root");
      if (root) root.querySelector<HTMLElement>("#overlay")?.setAttribute("data-state", "hidden");
      
      try {
        if (!this.scale.isFullscreen && !document.body.classList.contains("pseudo-fullscreen")) {
          toggleFullscreen(this.scale);
        }
      } catch (e) {
        // Fullscreen API may throw if not triggered by direct user gesture
      }
      
      this.scene.start("GameScene");
    };

    const enableStartSceneInputs = (): void => {
      if (this.input.keyboard) {
        this.input.keyboard.enabled = true;
      }
      // Brief delay (200ms) before listening for space/tap to avoid the click that saved email immediately starting the game
      this.time.delayedCall(200, () => {
        this.input.keyboard?.addKey("SPACE").once("down", startNow);
        this.input.keyboard?.addKey("ENTER").once("down", startNow);
        this.input.once("pointerdown", startNow);
      });
    };

    if (hasEmail) {
      enableStartSceneInputs();
      return;
    }

    // If overlay email prompt is active (!hasEmail), disable Phaser keyboard
    // completely so typing into HTML input fields is never captured or blocked.
    if (this.input.keyboard) {
      this.input.keyboard.enabled = false;
    }

    let dismissed = false;
    const onOverlayDismissed = (): void => {
      if (dismissed) return;
      dismissed = true;
      window.removeEventListener("traszka:play", onOverlayDismissed);
      if (observer) observer.disconnect();
      enableStartSceneInputs();
    };

    window.addEventListener("traszka:play", onOverlayDismissed);

    const root = document.getElementById("overlay-root");
    const overlay = root?.querySelector<HTMLElement>("#overlay");
    let observer: MutationObserver | null = null;
    if (overlay) {
      observer = new MutationObserver(() => {
        if (overlay.getAttribute("data-state") === "hidden") {
          onOverlayDismissed();
        }
      });
      observer.observe(overlay, { attributes: true, attributeFilter: ["data-state"] });
    } else {
      // Fallback if overlay element is missing
      enableStartSceneInputs();
    }
  }
}